import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getChatHistory, sendChatMessage, deleteChatMessage } from '../services/api';

const ChatModule = ({ height = "450px" }) => {
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = user && user.role && user.role.toLowerCase() === 'admin';
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(Notification.permission === 'granted');
  
  const scrollContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const lastMessageIdRef = useRef(null);

  const emojiCategories = {
    humeur: ["😊", "😂", "🤣", "😍", "😎", "🤔", "🙄", "🤨", "😴", "😇", "🥳", "😱", "💀", "🤡"],
    action: ["👍", "👎", "👊", "🙌", "👏", "🤝", "🔥", "✨", "💯", "✅", "❌", "🆘", "🎉", "⭐"],
    divers: ["❤️", "🌈", "🚀", "💎", "🎮", "📱", "💻", "🍿", "🍕", "🍺", "🌍", "🎁", "👀", "🤫"]
  };

  const emojiMap = {
    ":-)": "😊", ":)": "😊", ":-(": "☹️", ":(": "☹️", ":-D": "😁", ":D": "😁",
    ";-)": "😉", ";)": "😉", "<3": "❤️", "8-)": "😎", ":-P": "😛", ":P": "😛",
    ":-O": "😲", ":O": "😲", "(y)": "👍", "o/": "👋"
  };

  const formatMessageDate = useCallback((dateString) => {
    const msgDate = new Date(dateString);
    const today = new Date();
    const isToday = msgDate.getDate() === today.getDate() && 
                    msgDate.getMonth() === today.getMonth() && 
                    msgDate.getFullYear() === today.getFullYear();
    const timeStr = msgDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return timeStr;
    return `${msgDate.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${timeStr}`;
  }, []);

  const requestNotificationPermission = () => {
    Notification.requestPermission().then(p => setNotificationsEnabled(p === 'granted'));
  };

  const sendNotification = useCallback((msg) => {
    if (Notification.permission === 'granted' && document.hidden && msg.username !== user?.username) {
      new Notification(`Message de ${msg.username}`, { body: msg.content, icon: '/favicon.ico' });
    }
  }, [user?.username]);

  const fetchChat = useCallback(async () => {
    try {
      const response = await getChatHistory();
      let data = Array.isArray(response.data) ? response.data : [];
      if (data.length > 50) data = data.slice(-50);
      if (data.length > 0) {
        const latest = data[data.length - 1];
        if (lastMessageIdRef.current && latest.id !== lastMessageIdRef.current) {
          sendNotification(latest);
        }
        lastMessageIdRef.current = latest.id;
      }
      setMessages(data);
    } catch (err) { console.error("Erreur chat:", err); }
  }, [sendNotification]);

  useEffect(() => {
    fetchChat();
    const interval = setInterval(fetchChat, 5000);
    return () => clearInterval(interval);
  }, [fetchChat]);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 50;
    }
  };

  useEffect(() => {
    if (scrollContainerRef.current && isAtBottomRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [messages, isFullScreen]);

  const addEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !user) return;
    let processedMessage = newMessage;
    Object.keys(emojiMap).forEach(shortcut => {
      const escapedShortcut = shortcut.replace(/([.*+?^${}()|[\]\\])/g, '\\$1');
      const regex = new RegExp(escapedShortcut, 'g');
      processedMessage = processedMessage.replace(regex, emojiMap[shortcut]);
    });
    try {
      const payload = { 
        content: String(processedMessage), 
        user_id: Number(user.id), 
        is_mod_message: isAdmin ? 1 : 0 
      };
      await sendChatMessage(payload);
      setNewMessage("");
      setShowEmojiPicker(false);
      isAtBottomRef.current = true;
      fetchChat();
    } catch (err) { console.error("Erreur envoi:", err); }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer ce message définitivement ?")) {
      try {
        await deleteChatMessage(id);
        fetchChat();
      } catch (err) { console.error("Erreur suppression:", err); }
    }
  };

  return (
    <>
      <div className={`chat-module card bg-black border-secondary border-opacity-10 rounded-5 overflow-hidden shadow-2xl ${isFullScreen ? 'full-screen-chat' : 'h-100'}`}>
        <div className="card-header bg-black bg-opacity-50 border-bottom border-secondary border-opacity-10 p-3 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <div className="live-indicator-chat"><span className="dot-chat"></span></div>
            <h6 className="fw-black text-uppercase mb-0 tracking-widest text-white" style={{ fontSize: '0.75rem' }}>Chat Live</h6>
            <button onClick={requestNotificationPermission} className="btn btn-sm p-0 ms-1 shadow-none border-0">
                <i className={`bi ${notificationsEnabled ? 'bi-bell-fill text-primary' : 'bi-bell text-secondary opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
            </button>
          </div>
          
          <button 
            type="button" 
            className={`btn-fs-custom ${isFullScreen ? 'active' : ''}`} 
            onClick={() => setIsFullScreen(!isFullScreen)}
          >
            <i className={`bi ${isFullScreen ? 'bi-fullscreen-exit' : 'bi-arrows-angle-expand'}`}></i>
            <span className="btn-fs-text">{isFullScreen ? 'RÉDUIRE' : 'AGRANDIR'}</span>
          </button>
        </div>
        
        <div ref={scrollContainerRef} onScroll={handleScroll} className="card-body p-3 overflow-y-auto custom-scrollbar" 
             style={{ height: isFullScreen ? 'calc(100vh - 160px)' : height, backgroundColor: '#050505' }}>
          {messages.map((msg) => (
            <div key={msg.id} className="mb-3 animate-fade-in">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div className="d-flex align-items-center gap-2">
                  <span className={`fw-black text-uppercase ${!!msg.is_mod_message ? 'text-danger' : 'text-primary'}`} style={{ fontSize: '0.65rem' }}>
                    {!!msg.is_mod_message && <i className="bi bi-patch-check-fill me-1"></i>} {msg.username}
                  </span>
                  <span className="chat-date">{formatMessageDate(msg.created_at)}</span>
                </div>
                
                {isAdmin && (
                  <button onClick={() => handleDelete(msg.id)} className="btn btn-link p-0 text-danger border-0 shadow-none"><i className="bi bi-trash3" style={{ fontSize: '12px' }}></i></button>
                )}
              </div>
              <div className={`msg-bubble p-2 px-3 rounded-4 ${!!msg.is_mod_message ? 'mod-msg' : 'user-msg'}`}>
                <p className="small mb-0 text-light opacity-90 emoji-support" style={{ wordBreak: 'break-word' }}>{msg.content}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="card-footer bg-black bg-opacity-50 border-top border-secondary border-opacity-10 p-3 position-relative">
          {showEmojiPicker && (
            <div className="emoji-picker-container shadow-lg">
                {Object.entries(emojiCategories).map(([cat, emojis]) => (
                    <div key={cat} className="emoji-row">
                        {emojis.map(emoji => (
                            <span key={emoji} onClick={() => addEmoji(emoji)} className="emoji-item">{emoji}</span>
                        ))}
                    </div>
                ))}
            </div>
          )}
          {user ? (
            <form onSubmit={handleSendMessage} className="d-flex gap-2 align-items-center">
              <button type="button" className="btn-emoji" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>😊</button>
              <input type="text" className="chat-input form-control form-control-sm emoji-support" placeholder="Message..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} autoComplete="off" />
              <button type="submit" className="btn-send"><i className="bi bi-send-fill"></i></button>
            </form>
          ) : <div className="text-center py-1"><Link to="/login" className="login-link fw-black text-uppercase">Connexion requise</Link></div>}
        </div>
      </div>

      {isFullScreen && <div className="fs-overlay" onClick={() => setIsFullScreen(false)}></div>}

      <style>{`
        .btn-fs-custom {
          background: #0d6efd;
          color: white;
          border: none;
          padding: 5px 12px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 800;
          font-size: 0.65rem;
          transition: all 0.2s;
          box-shadow: 0 0 15px rgba(13, 110, 253, 0.4);
        }
        .btn-fs-custom:hover { background: #0b5ed7; transform: scale(1.05); }
        .btn-fs-custom.active { background: #dc3545; box-shadow: 0 0 15px rgba(220, 53, 69, 0.4); }
        .btn-fs-text { letter-spacing: 1px; }

        .emoji-support, .emoji-item, .btn-emoji {
          font-family: "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", "Android Emoji", sans-serif !important;
        }
        .fw-black { font-weight: 900; }
        .chat-date { color: #94a3b8 !important; font-size: 9px; font-weight: 600; text-transform: uppercase; opacity: 0.7; }
        .emoji-picker-container { 
            position: absolute; bottom: 75px; left: 15px; background: #111; 
            border: 1px solid rgba(255,255,255,0.1); border-radius: 15px; 
            padding: 12px; width: 280px; max-height: 250px; overflow-y: auto; z-index: 1000; 
        }
        .emoji-row { display: grid; grid-template-columns: repeat(7, 1fr); gap: 5px; margin-bottom: 8px; }
        .emoji-item { cursor: pointer; font-size: 1.2rem; text-align: center; transition: 0.2s; padding: 4px; }
        .emoji-item:hover { background: rgba(255,255,255,0.1); border-radius: 8px; transform: scale(1.2); }
        .btn-emoji { background: transparent; border: none; font-size: 1.1rem; opacity: 0.6; padding: 0; transition: 0.3s; }
        .btn-emoji:hover { opacity: 1; transform: scale(1.1); }
        .msg-bubble { border: 1px solid rgba(255,255,255,0.05); max-width: 95%; display: inline-block; }
        .user-msg { background: rgba(255, 255, 255, 0.03); }
        .mod-msg { background: linear-gradient(45deg, rgba(220, 53, 69, 0.1), rgba(220, 53, 69, 0.05)); border-left: 2px solid #dc3545 !important; }
        .chat-input { background: rgba(255,255,255,0.03) !important; border: 1px solid rgba(255,255,255,0.1) !important; color: white !important; border-radius: 20px !important; }
        .btn-send { background: #0d6efd; border: none; color: white; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        
        .fs-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.8); z-index: 10000; }
        
        /* CORRECTIONS ICI */
        .full-screen-chat { 
            position: fixed !important; 
            top: 0; 
            left: 0; 
            width: 100vw !important; 
            height: 100vh !important; 
            z-index: 10001 !important; /* On monte au dessus du header habituel */
            border-radius: 0 !important;
            margin-top: 0 !important;
            padding-top: 60px; /* On descend un peu le contenu pour éviter le header du site */
        }
        
        .animate-fade-in { animation: fadeIn 0.3s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; } }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 10px; }
      `}</style>
    </>
  );
};

export default ChatModule;